import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useCustomerPickerViewModel } from './viewModel';

/** Manager-header customer account switcher, ported from aligned's
 * `MultiManagerButton`/`MultiManagerWidget`. */
export function CustomerPicker() {
  const vm = useCustomerPickerViewModel();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  if (vm.loading || vm.accounts.length === 0) return null;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((isOpen) => !isOpen)}
        aria-label="Choose customer account"
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex max-w-[14rem] items-center gap-1 rounded-md px-3 py-2 text-left text-sm font-medium text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/50"
      >
        <span className="truncate">{vm.currentCustomerName || 'Choose customer'}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open ? (
        <div className="absolute right-0 z-50 mt-2 w-[300px] max-w-[calc(100vw-2rem)] rounded-lg border border-slate-200 bg-white p-5 text-slate-900 shadow-xl">
          <p className="text-sm font-bold">Choose Customer Account:</p>
          <div role="listbox" aria-label="Customer accounts" className="mt-4 max-h-72 space-y-2 overflow-y-auto pr-1">
            {vm.accounts.map((account) => (
              <button
                key={account.customerId}
                type="button"
                role="option"
                aria-selected={account.customerId === vm.currentCustomerId}
                onClick={() => {
                  setOpen(false);
                  void vm.switchCustomer(account);
                }}
                className={`block w-full rounded-md px-2 py-1 text-left text-base hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary/30 ${account.customerId === vm.currentCustomerId ? 'font-semibold text-primary' : 'text-slate-700'}`}
              >
                {account.customerName}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
